import React from 'react';

export default function StatusOverview({ status, onAddMethod }) {
  const getSecurityScore = () => {
    if (!status?.has2fa) return { score: 0, label: 'Not Protected', color: 'red' };
    if (status?.enabledMethods >= 2) return { score: 100, label: 'Excellent', color: 'green' };
    if (status?.enabledMethods === 1) return { score: 75, label: 'Good', color: 'blue' };
    return { score: 50, label: 'Fair', color: 'yellow' };
  };

  const securityScore = getSecurityScore();

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Security Score */}
        <div className="text-center">
          <div className="relative inline-flex items-center justify-center w-24 h-24 mb-2">
            <svg className="w-24 h-24 transform -rotate-90">
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke="#e5e7eb"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke={securityScore?.color === 'green' ? '#10b981' : securityScore?.color === 'blue' ? '#3b82f6' : securityScore?.color === 'yellow' ? '#f59e0b' : '#ef4444'}
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 40}`}
                strokeDashoffset={`${2 * Math.PI * 40 * (1 - securityScore?.score / 100)}`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-gray-900">{securityScore?.score}</span>
            </div>
          </div>
          <p className="text-sm font-medium text-gray-700">Security Score</p>
          <p className={`text-xs font-semibold ${
            securityScore?.color === 'green' ? 'text-green-600' :
            securityScore?.color === 'blue' ? 'text-blue-600' :
            securityScore?.color === 'yellow' ? 'text-yellow-600' : 'text-red-600'
          }`}>
            {securityScore?.label}
          </p>
        </div>

        {/* 2FA Status */}
        <div className="text-center">
          <div className={`w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center ${
            status?.has2fa ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {status?.has2fa ? (
              <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <p className="text-sm font-medium text-gray-700">2FA Status</p>
          <p className={`text-xs font-semibold ${
            status?.has2fa ? 'text-green-600' : 'text-red-600'
          }`}>
            {status?.has2fa ? 'Enabled' : 'Disabled'}
          </p>
        </div>

        {/* Active Methods */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-2 bg-indigo-100 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-indigo-600">{status?.enabledMethods || 0}</span>
          </div>
          <p className="text-sm font-medium text-gray-700">Active Methods</p>
          <p className="text-xs text-gray-500">
            {status?.primaryMethod ? status?.primaryMethod?.replace('_', ' ') : 'None'}
          </p>
        </div>

        {/* Backup Codes */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-2 bg-yellow-100 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-yellow-600">{status?.backupCodesRemaining || 0}</span>
          </div>
          <p className="text-sm font-medium text-gray-700">Backup Codes</p>
          <p className="text-xs text-gray-500">Remaining</p>
        </div>
      </div>
      {/* Quick Actions */}
      {!status?.has2fa && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start">
            <svg className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800 mb-2">
                Your account is not protected with two-factor authentication
              </p>
              <button
                onClick={onAddMethod}
                className="bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-yellow-700"
              >
                Enable 2FA Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}